/**
 * Date helpers for `<input type="date">` round-trips.
 *
 * The API returns instants as UTC ISO strings (`2021-02-06T23:00:00Z`), but a date input shows and
 * returns a **local** calendar date. Slicing the first ten characters off the ISO string takes the
 * UTC date, which east of Greenwich is the previous day for any instant stored at local midnight —
 * so loading a form and saving it unchanged walks the date one day earlier, every single save.
 * These functions are the fix; prefer them over `substring(0, 10)` and over
 * `new Date(...).toISOString().substring(0, 10)`, which has the same defect.
 */

/**
 * Formats an instant as the `YYYY-MM-DD` **local** calendar date an `<input type="date">` expects.
 *
 * @param value UTC ISO string, `Date`, or null/undefined.
 * @returns The local date, or `''` when there is nothing to show (so it binds straight to an input).
 */
export function toDateInputValue(value: string | Date | null | undefined): string {
    if (!value) return '';

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    // Built from the local getters on purpose. toISOString() would re-project to UTC and
    // reintroduce the off-by-one-day this helper exists to prevent.
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * Turns a date input's `YYYY-MM-DD` back into the instant at **local** midnight on that day — the
 * exact inverse of {@link toDateInputValue}, so a load/save round-trip leaves the stored value alone.
 *
 * Do not reach for `new Date('2021-02-06')` here: the language parses the date-only form as *UTC*
 * midnight, not local, so east of Greenwich it names the wrong day and drops the stay's start back
 * by the offset on every save.
 *
 * @param value `YYYY-MM-DD` from an `<input type="date">`, or empty.
 * @returns The instant, or null when there is no date.
 */
export function fromDateInputValue(value: string | null | undefined): Date | null {
    if (!value) return null;

    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return null;

    // The multi-argument constructor is local-time by definition — that is the whole point.
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A date input's `YYYY-MM-DD` as the ISO instant to send to the API, or null when empty.
 * Convenience wrapper over {@link fromDateInputValue} for request payloads.
 */
export function dateInputValueToIso(value: string | null | undefined): string | null {
    return fromDateInputValue(value)?.toISOString() ?? null;
}

/**
 * Today's date as `YYYY-MM-DD` in the viewer's own timezone — the default value for a date input.
 * Correct near midnight, where `new Date().toISOString().substring(0, 10)` can name yesterday or
 * tomorrow depending on which side of UTC the viewer sits.
 */
export function todayAsDateInputValue(): string {
    return toDateInputValue(new Date());
}
